import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
    DialogContent,
    DialogTitle,
    FormControl,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CustomDialog, StyledDataGrid } from '../../../components/StyledComponents';

import QuestionIcon16pxSvg from '../../../assets/images/icons/QuestionIcon16pxSvg';
import DefaultTooltip from '../../../components/DefaultTooltip';

import {
    ControlpanelPrimaryButton,
    GrayIconButton
} from '../../../components/StyledButton';

import CloseIcon from "@mui/icons-material/Close";

import { toast } from 'react-toastify';
import RefreshCircleIcon20pxSvgV2 from '../../../assets/images/icons/RefreshCircleIcon20pxSvgV2';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import CustomPagination from '../CustomPagination';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import { useAuth } from '../../../context/AuthContext';
import { MdInsertLink } from "react-icons/md";
import LinkIcon20pxSvg from '../../../assets/images/icons/LinkIcon20pxSvg';
import { format } from 'date-fns';
import CustomSelect from '../../../components/CustomSelect';
import ServerSearchInput from '../../../components/ServerSearchInput';
import { useLoading } from '../../../context/LoadingContext';
import { addInvitation, getEventsVisitors, getInvitation } from '../../../axios/Visitor/VisitorApi';
import CustomGridToolbarQuickFilterNew2 from '../CustomGridToolbarQuickFilterNew2';
import DeleteIcon16pxSvg from '../../../assets/images/icons/DeleteIcon16pxSvg';
import RemoveInvitationDialog from './RemoveInvitationDialog';

const ShareVisitorEventDialog = ({
    open,
    handleClose,
    onRefresh,
    id,
    dataInfo,
    refreshData
}) => {
    const [show, hide] = useLoading();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [rowCount, setRowCount] = useState(0);
    const [filteredRowCount, setFilteredRowCount] = useState(0);
    const [emailInput, setEmailInput] = useState("");
    const [emails, setEmails] = useState([]);
    const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
    const { profile, genaralSettingDateFormat } = useAuth();
    const [copied, setCopied] = useState(false);
    const [joinDate, setJoinDate] = useState("");
    const [searchText, setSearchText] = useState("");
    const [selectStatus, setSelectStatus] = useState("ALL");
    const [selectedRow, setSelectedRow] = useState(null);

    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [removeInvitation, setRemoveInvitation] = useState(false);

    const handleOpenInvitation = () => {
        setRemoveInvitation(true);
    };

    const columnData = useMemo(() => [
        { field: 'email', headerName: t('common.name'), hideable: true, flex: 1, minWidth: isMobile ? 140 : 180 },
        {
            field: 'status', headerName: t('common.status'), hideable: true, flex: 1, minWidth: isMobile ? 100 : 120, renderCell: (params) => {
                const status = params.value?.toUpperCase();

                const statusColor = {
                    APPROVED: '#058900',
                    PENDING: '#F97C00',
                    REJECTED: '#D00D00',
                };

                return (
                    <span
                        style={{
                            color: statusColor[status] || '#000',
                            fontWeight: 600,
                        }}
                    >
                        {t(`common.${params.value?.toLowerCase()}`)}
                    </span>
                );
            },
        },
        {
            field: "options",
            headerName: t("common.options"),
            sortable: false,
            filterable: false,
            minWidth: isMobile ? 100 : 200,
            flex: isMobile ? 0 : undefined,
            renderCell: (params) => {
                const isPending = params.row.status?.toUpperCase() === 'PENDING';

                return (
                    <div
                        className="flex gap-2 items-center justify-start h-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {isPending && (
                            <Tooltip
                                title={t('button.resend-email')}
                                placement="right"
                                arrow
                            >
                                <button
                                    type="button"
                                    className="p-2 rounded-lg hover:bg-[#E5E7EB] transition cursor-pointer flex items-center justify-center pointer-none opacity-[0.5]"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <RefreshCircleIcon20pxSvgV2
                                        className="w-5 h-5 text-[#0057FF]"
                                    />
                                </button>
                            </Tooltip>
                        )}

                        <Tooltip
                            title={t('button.delete')}
                            placement="right"
                            arrow
                        >
                            <button
                                type="button"
                                className="p-2 rounded-lg hover:bg-red-100 transition cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenInvitation();
                                    setSelectedRow(params.row);
                                }}
                            >
                                <DeleteIcon16pxSvg
                                    className="w-5 h-5"
                                    color="#D00D00"
                                />
                            </button>
                        </Tooltip>
                    </div>
                );
            },
        },
    ], [t, isMobile]);

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleAddEmail = () => {

        if (!emailInput.trim()) return;

        // tách theo dấu ,
        const inputEmails = emailInput
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);

        // validate
        const invalidEmails = inputEmails.filter(
            (email) => !isValidEmail(email)
        );

        if (invalidEmails.length > 0) {
            toast.error(
                `${t("common.invalid-email")}: ${invalidEmails.join(", ")}`
            );
            return;
        }

        // remove duplicate
        const uniqueEmails = inputEmails.filter(
            (email) => !emails.includes(email)
        );

        if (uniqueEmails.length === 0) {
            toast.error(t("common.email-already-added"));
            return;
        }

        setEmails((prev) => [...prev, ...uniqueEmails]);

        setEmailInput("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddEmail();
        }
    };

    const handleRemoveEmail = (email) => {
        setEmails((prev) =>
            prev.filter((item) => item !== email)
        );
    };

    const handleSend = async () => {

        try {

            const payload = {
                eventId: id,
                emails,
                joinDate,
                langId: localStorage.getItem("i18nextLngId")
            };

            const response = await addInvitation(payload);

            // ===== SUCCESS =====
            if (
                response?.data?.code === 200
            ) {
                toast.success(t("common.sent-mail-successfully"));
                setEmails([]);
                setEmailInput("");
                setJoinDate("");
                fetchData();
                onRefresh();
            } else {

                toast.error(
                    response?.data?.data?.message ||
                    t("common.something-went-wrong")
                );
            }

        } catch (error) {

            console.error(error);

            toast.error(
                error?.response?.data?.message ||
                t("common.something-went-wrong") ||
                "Something went wrong"
            );
        }
    };

    const handleRowsPerPageChange = (newPageSize) => {
        // newPageSize ở đây tuỳ CustomPagination, có thể là event hoặc number
        const size =
            typeof newPageSize === "number"
                ? newPageSize
                : parseInt(newPageSize.target.value, 10);

        setPage((prevPage) => {
            // index của record đầu tiên đang xem
            const firstRowIndex = prevPage * pageSize;

            // page mới sao cho vẫn chứa record đó
            const newPage = Math.floor(firstRowIndex / size);

            // phòng trường hợp vượt quá số trang
            const maxPage = Math.max(
                0,
                Math.ceil(filteredRowCount / size) - 1
            );

            return Math.min(newPage, maxPage);
        });

        setPageSize(size);
    };

    // const handleRowClick = (params) => {
    //     const rowData = params.row;
    //     if (rowData) {
    //         onRowClick(rowData);
    //     }
    // };

    const generateDateOptions = (start, end) => {
        const result = [];

        const startDate = new Date(start);
        const endDate = new Date(end);

        let current = new Date(startDate);

        while (current <= endDate) {
            result.push({
                label: format(current, genaralSettingDateFormat),
                value: format(current, "yyyy-MM-dd"),
            });

            current.setDate(current.getDate() + 1);
        }

        return result;
    };

    const dateOptions = React.useMemo(() => {
        if (!dataInfo?.startDate || !dataInfo?.endDate) return [];

        return generateDateOptions(dataInfo.startDate, dataInfo.endDate);
    }, [dataInfo]);

    const handleCopyInviteUrl = async () => {
        const inviteUrl = `${window.location.origin}/portal/visitor-registration?visitorURL=${id}&comid=${profile?.companyId}`;


        try {
            await navigator.clipboard.writeText(inviteUrl);

            setCopied(true);

            setTimeout(() => {
                setCopied(false);
            }, 2000);

        } catch (error) {
            console.error("Copy failed:", error);
        }
    };

    const handleSearchChange = (value) => {
        setSearchText(value);
    };

    const handleInputChange = (e) => {
        const { value, name } = e.target;

        if (name === "statusEmail") {
            setSelectStatus((value));
        }
    };

    const templateToolbar = useCallback(
        () => (
            <div className="flex flex-col md:flex-row gap-3 my-4">
                {/* <ServerSearchInput
                    onChange={handleSearchChange}
                    fullWidth={true}
                /> */}

                <div className="w-full">
                    <CustomGridToolbarQuickFilterNew2
                        onSearchChange={handleSearchChange}
                    />
                </div>

                <FormControl className="w-full">
                    <CustomSelect
                        name="statusEmail"
                        id="statusEmail"
                        value={selectStatus}
                        onChange={handleInputChange}
                        options={statusLeaveData}
                        dataType="statusEmail"
                    />
                </FormControl>
            </div>
        ),
        [t, selectStatus]
    );

    const statusLeaveData = [
        {
            statusEmailId: "ALL",
            statusName: t("common.all-status"),
        },
        {
            statusEmailId: "APPROVED",
            statusName: t("common.approved"),
        },
        {
            statusEmailId: "REJECTED",
            statusName: t("common.rejected"),
        },
        {
            statusEmailId: "PENDING",
            statusName: t("common.pending"),
        },
    ];

    const fetchData = async () => {
        const payload = {
            eventId: dataInfo?.id || null,
            pageNo: page + 1,
            recordPerPage: pageSize,
            status: selectStatus === "ALL" ? null : selectStatus,
        };
        try {
            show();
            const response = await getInvitation(payload);

            setData(response?.data?.data || []);

            setTotalPages(
                response?.data?.pagination?.totalPages || 1
            );

            setTotalElements(
                response?.data?.pagination?.totalElements || 0
            );

        } catch (error) {
            console.error(error);
        } finally {
            hide();
        }
    };

    useEffect(() => {

        if (!open) return;

        if (!dataInfo?.id) return;

        fetchData();

    }, [
        open,
        dataInfo?.id,
        page,
        pageSize,
        refreshData,
        selectStatus
    ]);

    useEffect(() => {
        setRowCount(data.length);
        setFilteredRowCount(data.length);
    }, [data]);

    return (
        <>
            <CustomDialog
                maxWidth={"md"}
                fullWidth
                open={open}
                onClose={handleClose}
                keepMounted
                sx={{
                    ".MuiPaper-root": {
                        width: { xs: "calc(100% - 16px)", md: "800px" },
                        maxWidth: { xs: "100%", md: "800px" },
                        margin: { xs: 1, md: "auto" },
                        maxHeight: { xs: "calc(100dvh - 16px)", md: "90vh" },
                        display: "flex",
                        flexDirection: "column",
                    },
                    "& .MuiDialogContent-root": {
                        overflow: "auto",
                        flex: 1,
                    },
                }}
            >
                <DialogTitle sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                            <p className="text-lg sm:text-xl font-semibold truncate">
                                {t("common.share")}
                            </p>

                            <DefaultTooltip
                                readMore={true}
                                title={t("common.share")}
                                content={
                                    <div className="flex flex-col gap-3">
                                        <p>
                                            {t("common.share")}
                                        </p>
                                    </div>
                                }
                            >
                                <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0" />
                            </DefaultTooltip>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto shrink-0">
                            <button
                                type="button"
                                onClick={handleCopyInviteUrl}
                                className="cursor-pointer hover:opacity-70 transition flex items-center gap-2 text-[#0057FF] text-sm sm:text-base min-w-0"
                            >
                                <LinkIcon20pxSvg className="w-5 h-5 shrink-0" />

                                <p className="truncate">
                                    {copied
                                        ? t("common.copied")
                                        : t("common.copy-link")}
                                </p>
                            </button>

                            <GrayIconButton onClick={handleClose} className="shrink-0">
                                <CloseIcon />
                            </GrayIconButton>
                        </div>

                    </div>
                </DialogTitle>

                <DialogContent sx={{ px: { xs: 2, md: 3 } }}>
                    <div className="flex flex-col md:flex-row gap-3 md:items-start py-4 border-b border-[#E5E7EB] pb-3">
                        {/* EMAIL */}
                        <div className="flex-1 w-full min-w-0">
                            <label className="text-sm font-medium">
                                {t("common.email")} <span className="text-[#EF4444] ml-1">*</span>
                            </label>

                            <div className="mt-2">
                                <div
                                    className="
            w-full
            border
            border-[#E5E7EB]
            rounded-lg
            focus-within:border-[#0057FF]
            overflow-hidden
        "
                                >
                                    <div
                                        className="
            px-3
            py-1.5
            flex
            flex-wrap
            gap-2
            items-center
            min-h-[38px]
            max-h-[80px]
            overflow-y-auto
        "
                                    >
                                    {emails.map((email) => (
                                        <div
                                            key={email}
                                            className="
                    flex
                    items-center
                    gap-2
                    bg-[#F9FAFB]
                    text-[#1F2937]
                    border border-[#E5E7EB]
                    px-3
                    py-1
                    rounded-full
                    text-sm
                    shrink-0
                "
                                        >
                                            <span>{email}</span>

                                            <button
                                                onClick={() => handleRemoveEmail(email)}
                                                className="
                        text-[#FFFFFF]
                        bg-[#D1D5DB]
                        rounded-full
                        w-[18px]
                        h-[18px]
                        hover:text-red-500
                        justify-center
                        flex
                        items-center
                        transition
                        leading-none
                    "
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}

                                    <input
                                        type="text"
                                        value={emailInput}
                                        placeholder={t("common.email")}
                                        onChange={(e) => setEmailInput(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        className="
                flex-1
                min-w-[120px]
                sm:min-w-[180px]
                outline-none
                border-none
                bg-transparent
                h-[26px]
            "
                                    />
                                    </div>
                                </div>
                            </div>

                            <p className="text-sm text-[#9CA3AF] md:mt-1 md:h-[20px] mb-1 md:mb-0">
                                {t("common.enter-email")}
                            </p>
                        </div>

                        {/* DATE */}
                        <div className="w-full md:min-w-[220px] md:w-auto shrink-0">
                            <label className="text-sm font-medium">
                                {t("common.select-date")}
                                <span className="text-[#EF4444] ml-1">*</span>
                            </label>

                            <div className="mt-2">
                                <CustomSelect
                                    name="joinDate"
                                    id="joinDate"
                                    value={joinDate}
                                    onChange={(e) => setJoinDate(e.target.value)}
                                    options={dateOptions}
                                    dataType="joinDate"
                                    className="w-full"
                                />
                            </div>

                            <div className="md:h-[20px] md:mt-1" />
                        </div>

                        {/* BUTTON */}
                        <div className="w-full md:w-auto md:pt-[27px] shrink-0">
                            <ControlpanelPrimaryButton
                                disabled={emails.length === 0 || !joinDate}
                                className="h-10 w-full md:w-auto"
                                onClick={() => {
                                    if (emailInput.trim()) {
                                        handleAddEmail();
                                    } else {
                                        handleSend();
                                    }
                                }}
                            >
                                <p>{t("button.invite")}</p>
                            </ControlpanelPrimaryButton>

                            {/* helper fake */}
                            <div className="h-[20px] mt-1" />
                        </div>
                    </div>

                    <Fragment>
                        <StyledDataGrid
                            showColumnVerticalBorder
                            sx={{
                                height: "60vh",

                                ".MuiDataGrid-main": {
                                    height: "60vh",
                                },

                                "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
                                {
                                    display: "none !important",
                                },

                                "& .MuiDataGrid-scrollbarFiller": {
                                    display: "none !important",
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    paddingRight: 0,
                                },
                                "& .MuiDataGrid-topContainer": {
                                    paddingRight: 0,
                                },

                                "&": {
                                    "--DataGrid-scrollbarSize": "0px",
                                },

                                "& .MuiDataGrid-virtualScroller": {
                                    scrollbarWidth: "none",
                                },
                                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                                    width: 0,
                                    height: 0,
                                },
                                "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
                                {
                                    scrollbarWidth: "thin",
                                },
                                "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
                                {
                                    width: 10,
                                    height: 10,
                                },

                                '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                                    display: "none !important",
                                },

                                "& input[type=number]::-webkit-inner-spin-button, \
                 & input[type=number]::-webkit-outer-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                },

                                "& input[type=number]": {
                                    textAlign: "center",
                                    MozAppearance: "textfield",
                                    WebkitAppearance: "none",
                                    padding: "5px !important",
                                    height: "100%",
                                    pointerEvents: "none",
                                    caretColor: "transparent",
                                    userSelect: "none",
                                    paddingTop: "8px !important",
                                    paddingBottom: "8px !important"
                                },
                            }}
                            rows={data || []}
                            rowCount={rowCount}
                            paginationMode="client"
                            columns={columnData}
                            // loading={gridLoading}
                            getRowId={(r) => r.id}
                            slots={{
                                toolbar: templateToolbar,
                                pagination: CustomPagination,
                                noRowsOverlay: NoRowOverlay,
                                columnsPanel: CustomColumnsPanel,
                            }}
                            slotProps={{
                                pagination: {
                                    page,
                                    count: rowCount,
                                    rowsPerPage: pageSize,
                                    onPageChange: setPage,
                                    onRowsPerPageChange: handleRowsPerPageChange,
                                },
                            }}
                            localeText={tableTranslated}
                        // onRowClick={handleRowClick} 
                        />
                    </Fragment>
                </DialogContent>
            </CustomDialog>

            {removeInvitation && <RemoveInvitationDialog dataInfo={dataInfo} id={selectedRow?.id} onClose={() => setRemoveInvitation(false)} onRefresh={onRefresh} refreshData={refreshData} />}</>

    );
};

export default ShareVisitorEventDialog;